import { Buttons, Image, Overlay, Scroller, Slider } from '@cian/slider';
import classNames from 'classnames';
import * as React from 'react';

import * as styles from './GalleryInner.css';
import { ButtonFullscreen } from './components/ButtonFullscreen';
import { Keyboard } from './components/Keyboard';
import { EMediaType, TMedia } from '../../types';
import { CianLayout } from '../CianLayout';
import { PaginationThumbs } from '../PaginationThumbs';
import { VideoPlayable } from '../VideoPlayable';

interface IGalleryInnerSlots {
  slotScrollerEnd?: React.ReactNode;
}

interface IGalleryInnerProps extends IGalleryInnerSlots {
  media: TMedia[];
  bordered?: boolean;
  fullscreenable?: boolean;
  keyboard?: boolean;
  objectFit?: React.ComponentProps<typeof Image>['objectFit'];
  thumbsBounded?: boolean;
  videoPlayable?: boolean;
  onSlideChange?(index: number): void;
  onScrollerClick?(): void;
  onFullscreenClick?(): void;
}

const GalleryInnerComponent: React.ForwardRefRenderFunction<
  React.ElementRef<typeof Slider> | null,
  IGalleryInnerProps
> = (
  {
    media,
    bordered,
    fullscreenable,
    keyboard = true,
    objectFit,
    thumbsBounded,
    videoPlayable,
    slotScrollerEnd,
    onSlideChange,
    onScrollerClick,
    onFullscreenClick,
  },
  ref,
) => {
  const sliderRef = React.useRef<React.ElementRef<typeof Slider>>(null);
  const paginationThumbsRef = React.useRef<React.ElementRef<typeof PaginationThumbs>>(null);

  const handleScrollEnd = React.useCallback(() => {
    paginationThumbsRef.current?.scrollCurrentThumbIntoView();
  }, []);

  const handleScrollerClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      if (target?.closest('a, button')) {
        return;
      }

      if (onScrollerClick) {
        onScrollerClick();
      }
    },
    [onScrollerClick],
  );

  React.useImperativeHandle<React.ElementRef<typeof Slider> | null, React.ElementRef<typeof Slider> | null>(
    ref,
    () => sliderRef.current,
    [],
  );

  return (
    <div className={styles['container']}>
      <Slider ref={sliderRef} loop onSlideChange={onSlideChange}>
        {keyboard && <Keyboard />}
        <div
          className={classNames(styles['scroller'], { [styles['scroller--bordered']]: bordered })}
          onClick={handleScrollerClick}
        >
          <Scroller onScrollEnd={handleScrollEnd}>
            {media.map((m, index) => {
              switch (m.type) {
                case EMediaType.Layout:
                case EMediaType.Photo: {
                  return (
                    <Image
                      key={`slide_${index}`}
                      src={m.src}
                      objectFit={m.type === EMediaType.Layout ? 'contain' : objectFit || 'cover'}
                      loading={index === 0 ? null : undefined}
                      decoding={index === 0 ? null : undefined}
                    />
                  );
                }
                case EMediaType.Video:
                  return <VideoPlayable key={`slide_${index}`} src={m.src} playable={videoPlayable} />;
                case EMediaType.CianLayout:
                  return <CianLayout key={`slide_${index}`} src={m.src} deoptimised={index === 0} />;
                case EMediaType.Component:
                  return m.component;
                case EMediaType.Tour:
                  return m.component;
                case EMediaType.FloorPlan:
                  return m.component;
              }
            })}
          </Scroller>
          {(fullscreenable || media.length > 1) && (
            <Overlay>
              {fullscreenable && <ButtonFullscreen onClick={onFullscreenClick} />}
              {media.length > 1 && <Buttons size="M" />}
            </Overlay>
          )}
          {slotScrollerEnd}
        </div>
        <div className={styles['thumbs']}>
          <div className={classNames(styles['thumbs-wrapper'], { [styles['thumbs-wrapper--bounded']]: thumbsBounded })}>
            <PaginationThumbs ref={paginationThumbsRef} media={media} />
          </div>
        </div>
      </Slider>
    </div>
  );
};

export const GalleryInner = React.memo(React.forwardRef(GalleryInnerComponent));
